import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  FolderKanban,
  CheckCircle2,
  Clock,
  AlertCircle,
  TrendingUp,
  ArrowRight,
  PauseCircle,
  Users,
  FileText,
  MessageSquare,
  Trash2,
} from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useProjects } from '@/hooks/useProjects';
import { useUIStore } from '@/stores/uiStore';
import { formatRelativeTime, truncate } from '@/lib/utils';
import type { Project, ProjectStatus } from '@/types';

const roleLabels: Record<string, string> = {
  owner: '소유자',
  editor: '편집자',
  developer: '개발자',
  viewer: '뷰어',
};

// Tab configuration with icons
const tabConfig: { value: ProjectStatus; label: string; icon: typeof Clock }[] = [
  { value: 'active', label: '진행', icon: Clock },
  { value: 'completed', label: '완료', icon: CheckCircle2 },
  { value: 'on_hold', label: '보류', icon: PauseCircle },
  { value: 'deleted', label: '삭제', icon: Trash2 },
];

function ProjectTable({ projects }: { projects: Project[] }) {
  if (projects.length === 0) {
    return (
      <div className="p-12 text-center">
        <FolderKanban className="mx-auto h-12 w-12 text-muted-foreground" />
        <p className="mt-4 text-sm text-muted-foreground">
          해당 상태의 프로젝트가 없습니다
        </p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[250px]">프로젝트명</TableHead>
          <TableHead>소유자</TableHead>
          <TableHead>내 역할</TableHead>
          <TableHead className="text-center">
            <span className="flex items-center justify-center gap-1">
              <Users className="h-4 w-4" />
              멤버
            </span>
          </TableHead>
          <TableHead className="text-center">
            <span className="flex items-center justify-center gap-1">
              <CheckCircle2 className="h-4 w-4" />
              작업
            </span>
          </TableHead>
          <TableHead className="text-center">
            <span className="flex items-center justify-center gap-1">
              <FileText className="h-4 w-4" />
              문서
            </span>
          </TableHead>
          <TableHead className="text-center">
            <span className="flex items-center justify-center gap-1">
              <MessageSquare className="h-4 w-4" />
              채팅
            </span>
          </TableHead>
          <TableHead>업데이트</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {projects.map((project) => (
          <TableRow key={project.id} className="cursor-pointer">
            <TableCell>
              <Link
                to={`/project/${project.id}`}
                className="font-medium hover:underline"
              >
                {project.name}
              </Link>
              {project.description && (
                <p className="text-xs text-muted-foreground mt-0.5">
                  {truncate(project.description, 50)}
                </p>
              )}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {project.ownerName || '-'}
            </TableCell>
            <TableCell>
              {project.currentUserRole && (
                <Badge variant="outline">
                  {roleLabels[project.currentUserRole] || project.currentUserRole}
                </Badge>
              )}
            </TableCell>
            <TableCell className="text-center">
              {project.memberCount || 0}
            </TableCell>
            <TableCell className="text-center">
              <span className="text-sm">
                {project.completedTaskCount}/{project.taskCount}
              </span>
              {project.taskCount > 0 && (
                <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full bg-primary transition-all"
                    style={{
                      width: `${(project.completedTaskCount / project.taskCount) * 100}%`,
                    }}
                  />
                </div>
              )}
            </TableCell>
            <TableCell className="text-center">
              {project.documentCount || 0}
            </TableCell>
            <TableCell className="text-center">
              {project.chatSessionCount || 0}
            </TableCell>
            <TableCell className="text-muted-foreground text-sm">
              {formatRelativeTime(project.updatedAt)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function DashboardPage() {
  const { projects, isLoading } = useProjects();
  const { openModal } = useUIStore();
  const [activeTab, setActiveTab] = useState<ProjectStatus>('active');

  // Group projects by status
  const projectsByStatus = {
    active: projects.filter((p) => p.status === 'active'),
    completed: projects.filter((p) => p.status === 'completed'),
    on_hold: projects.filter((p) => p.status === 'on_hold'),
    deleted: projects.filter((p) => p.status === 'deleted'),
  };

  // Calculate stats (excluding deleted projects)
  const visibleProjects = projects.filter((p) => p.status !== 'deleted');
  const totalProjects = visibleProjects.length;
  const totalTasks = visibleProjects.reduce((sum, p) => sum + (p.taskCount || 0), 0);
  const completedTasks = visibleProjects.reduce((sum, p) => sum + (p.completedTaskCount || 0), 0);
  const completionRate = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const stats = [
    {
      title: '전체 프로젝트',
      value: totalProjects,
      icon: FolderKanban,
      color: 'text-blue-600',
      bg: 'bg-blue-100',
    },
    {
      title: '진행중',
      value: projectsByStatus.active.length,
      icon: Clock,
      color: 'text-green-600',
      bg: 'bg-green-100',
    },
    {
      title: '보류',
      value: projectsByStatus.on_hold.length,
      icon: PauseCircle,
      color: 'text-yellow-600',
      bg: 'bg-yellow-100',
    },
    {
      title: '완료',
      value: projectsByStatus.completed.length,
      icon: CheckCircle2,
      color: 'text-purple-600',
      bg: 'bg-purple-100',
    },
    {
      title: '작업 완료율',
      value: `${completionRate}%`,
      description: `${completedTasks}/${totalTasks}`,
      icon: TrendingUp,
      color: 'text-orange-600',
      bg: 'bg-orange-100',
    },
  ];

  return (
    <div className="flex flex-col">
      <Header title="대시보드" />

      <div className="flex-1 space-y-6 p-6">
        {/* Stats Grid */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {stats.map((stat) => (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <div className={`rounded-lg p-2 ${stat.bg}`}>
                  <stat.icon className={`h-4 w-4 ${stat.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
                {'description' in stat && stat.description && (
                  <p className="text-xs text-muted-foreground mt-1">{stat.description}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Projects Section */}
        <div>
          <div className="mb-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">프로젝트</h2>
              <p className="text-sm text-muted-foreground">
                프로젝트를 관리하고 진행 상황을 추적하세요
              </p>
            </div>
            <Button onClick={() => openModal('createProject')}>
              <Plus className="mr-2 h-4 w-4" />
              새 프로젝트
            </Button>
          </div>

          {isLoading ? (
            <Card>
              <div className="p-4 space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex gap-4">
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-6 w-24" />
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-6 w-20" />
                  </div>
                ))}
              </div>
            </Card>
          ) : projects.length === 0 ? (
            <Card className="p-12 text-center">
              <FolderKanban className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">아직 프로젝트가 없습니다</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                첫 번째 프로젝트를 만들어 시작하세요.
              </p>
              <Button
                className="mt-4"
                onClick={() => openModal('createProject')}
              >
                <Plus className="mr-2 h-4 w-4" />
                프로젝트 만들기
              </Button>
            </Card>
          ) : (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as ProjectStatus)}>
              <TabsList className="mb-4">
                {tabConfig.map((tab) => (
                  <TabsTrigger key={tab.value} value={tab.value} className="gap-2">
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                    <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                      {projectsByStatus[tab.value].length}
                    </Badge>
                  </TabsTrigger>
                ))}
              </TabsList>

              {tabConfig.map((tab) => (
                <TabsContent key={tab.value} value={tab.value}>
                  <Card>
                    <ProjectTable projects={projectsByStatus[tab.value]} />
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          )}
        </div>

        {/* Quick Actions */}
        {visibleProjects.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">최근 활동</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {visibleProjects.slice(0, 3).map((project) => (
                    <div
                      key={project.id}
                      className="flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted text-sm font-medium">
                          {project.name.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{project.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatRelativeTime(project.updatedAt)} 업데이트됨
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">대기 중인 작업</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center py-8 text-center">
                  <div>
                    <AlertCircle className="mx-auto h-8 w-8 text-muted-foreground" />
                    <p className="mt-2 text-sm text-muted-foreground">
                      작업을 보려면 프로젝트를 선택하세요
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
